/**
 * #1209 — the cross-tenant bin nudge's marker, ONE definition for the server that sets it (`@papan/files`), the wire
 * type (`@papan/realtime`'s `FileEvent`) and the clients that read it (the Shared page, the launcher). Browser-safe:
 * no imports.
 *
 * `sharedIn`: whether the deleted / restored subtree held an org→org share ROOT to THE RECIPIENT this nudge goes to.
 * Absent from a server older than #1209, or when the server couldn't tell (its lookup failed, or the delete left no
 * Bin row) — clients then fall back to matching the item's name.
 */
export interface BinNudge {
  scope: 'bin'
  sharedIn?: boolean
  /** Set when the recipient holds NO live share from the source any more — it was reached only because the binned
   *  subtree held its (now dropped) share. Such a nudge names at most that org's OWN item (#1165) and carries a
   *  system actor; it is for the SPA only: app (SDK) clients never get it. Explicit, never inferred from a missing name (a publisher may omit the name for other reasons).
   *  Also set on the NAMELESS fallback nudge (an org with a share root in the subtree that nothing else names to it),
   *  whatever other share that org holds — the flag means "SPA only, system actor", not only "no live share".
   *  Server-side routing only: the realtime fan-out strips it before anything is sent, so a client never sees it
   *  (kept on this one shared type rather than a server-only extension, since `FileEvent` is shared too). */
  noLiveShare?: true
}

/**
 * What a client should do with a file event for its Shared page / launcher: `true` = a share root of this drive
 * changed, refresh; `false` = it didn't, leave them alone (even when a name happens to match); `undefined` = not a
 * tagged bin nudge — decide as before (the name match, or the native-event rules).
 */
export function binNudgeSharedIn(event: { nudge?: BinNudge }): boolean | undefined {
  return isBinNudge(event) ? event.nudge.sharedIn : undefined
}

/** Is this file event a cross-tenant bin nudge (it carries the SOURCE tenant's id, not one of this drive's)? */
export function isBinNudge(event: { nudge?: BinNudge }): event is { nudge: BinNudge } {
  return event.nudge?.scope === 'bin'
}
